"use client";

import { errorHandlerReport } from "@mgdx-libs/error-handler";
import { logger } from "@mgdx-libs/logger";
import {
  createContext,
  type MutableRefObject,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isMediaPermissionDeniedError } from "./isMediaPermissionDeniedError";
import { MediaDeviceManager } from "./MediaDeviceManager";

type MediaDeviceContextValue = {
  mediaDeviceManager: MediaDeviceManager;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
};

const MediaDeviceContext = createContext<MediaDeviceContextValue>({
  videoRef: { current: null },
  mediaDeviceManager: {} as MediaDeviceManager,
});

export const MediaDeviceProvider = ({ children }: PropsWithChildren) => {
  const [mediaDeviceManager] = useState(() => {
    logger.debug("New MediaDeviceManager");
    return new MediaDeviceManager();
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const value = useMemo<MediaDeviceContextValue>(
    () => ({
      mediaDeviceManager,
      videoRef,
    }),
    [mediaDeviceManager]
  );

  return <MediaDeviceContext.Provider value={value}>{children}</MediaDeviceContext.Provider>;
};

export const useMediaDevice = (): MediaDeviceContextValue => useContext(MediaDeviceContext);

export const useAutoFetchMediaStream = () => {
  const { mediaDeviceManager } = useMediaDevice();

  useEffect(() => {
    if (!mediaDeviceManager.isGetUserMediaSupported) return;
    mediaDeviceManager.fetchMediaStream().catch((error: unknown) => {
      // 権限拒否はユーザー操作として想定内のため Sentry に報告しない
      // UI 通知は devicePermissionStatus 経由の既存経路(VideoCallingApp の warnMessage)で行う
      // getUserMedia は DOMException を投げ、環境により instanceof Error が false になるため name で判定する
      if (isMediaPermissionDeniedError(error)) {
        logger.debug("[useAutoFetchMediaStream] Permission denied %o", error);
        return;
      }
      void errorHandlerReport(error instanceof Error ? error : new Error(String(error)), {
        tags: { api: "getUserMedia", deviceType: "camera/microphone" },
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useAutoStopMediaStream = () => {
  const { mediaDeviceManager, videoRef } = useMediaDevice();

  useEffect(() => {
    const video = videoRef.current;

    return () => {
      mediaDeviceManager.clearMediaStream();

      if (video) {
        video.srcObject = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
